import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createMenu, listMenuPrices, listMenus, setMenuPrice, updateMenu } from '../../api/menu';
import { usePermissions } from '../../context/PermissionContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDate,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Menu, MenuPrice } from '../../types/domain';

const MENU_STATUSES: Menu['status'][] = ['ACTIVE', 'INACTIVE'];

function formatRupiah(value: number): string {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
}

/**
 * Menu Master (FASE 7, migration 0039). menus/menu_prices are tenant-wide —
 * no "Cabang Aktif" scoping here, matching Items/Suppliers. Every write
 * goes through fn_create_menu / fn_update_menu / fn_set_menu_price.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function MenuMasterPage() {
  const { hasPermission } = usePermissions();
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<Menu | null>(null);

  const canCreate = hasPermission('menu', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setMenus(await listMenus());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return menus.filter((m) => {
      if (q && !m.name.toLowerCase().includes(q) && !m.menu_code.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && m.status !== statusFilter) return false;
      return true;
    });
  }, [menus, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Menu Master" subtitle="Master menu — dasar untuk resep dan HPP. Migrasi 0039." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Menu baru'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewMenuForm
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(menus, MENU_STATUSES);
              return [
                { label: 'Total Menu', value: menus.length },
                { label: 'Aktif', value: c.ACTIVE, tone: 'success' as const },
                { label: 'Nonaktif', value: c.INACTIVE, tone: 'neutral' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari kode/nama menu..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </Select>
            }
          />
          {menus.length === 0 && <EmptyState text="Belum ada menu." />}
          {menus.length > 0 && total === 0 && <EmptyState text="Tidak ada menu yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Kode</th>
                  <th style={thStyle}>Nama Menu</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((m) => (
                  <ClickableRow key={m.menu_id} onClick={() => setSelected(m)}>
                    <td style={tdStyle}>{m.menu_code}</td>
                    <td style={tdStyle}>{m.name}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={m.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <MenuDetailDrawer
        menu={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        canEdit={canCreate}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewMenuForm({ onCreated }: { onCreated: () => void }) {
  const [menuCode, setMenuCode] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createMenu(menuCode.trim(), name.trim());
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
      <div style={{ width: 160 }}>
        <FieldLabel label="Kode Menu">
          <TextInput value={menuCode} onChange={(e) => setMenuCode(e.target.value)} required autoFocus />
        </FieldLabel>
      </div>
      <div style={{ flex: 1 }}>
        <FieldLabel label="Nama Menu">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function MenuDetailDrawer({
  menu,
  open,
  onClose,
  canEdit,
  onChanged,
}: {
  menu: Menu | null;
  open: boolean;
  onClose: () => void;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [prices, setPrices] = useState<MenuPrice[] | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [newPrice, setNewPrice] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    if (!menu) {
      setPrices(null);
      return;
    }
    setPrices(null);
    listMenuPrices(menu.menu_id)
      .then(setPrices)
      .catch((e) => setPriceError(e instanceof Error ? e.message : String(e)));
  }, [menu]);

  if (!menu) return null;
  const currentPrice = prices?.find((p) => !p.effective_to) ?? null;

  const handleAddPrice = async (e: FormEvent) => {
    e.preventDefault();
    const price = Number(newPrice);
    if (!Number.isFinite(price) || price < 0) return;
    setSubmitting(true);
    setPriceError(null);
    try {
      await setMenuPrice(menu.menu_id, price, effectiveFrom);
      setNewPrice('');
      setPrices(await listMenuPrices(menu.menu_id));
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async () => {
    setSavingStatus(true);
    try {
      await updateMenu(menu.menu_id, menu.name, menu.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
      onChanged();
      onClose();
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={menu.name}>
      <DrawerField label="Kode Menu" value={menu.menu_code} />
      <DrawerField label="Status" value={<StatusBadge status={menu.status} />} />
      {canEdit && (
        <Button variant="secondary" onClick={() => void toggleStatus()} loading={savingStatus} style={{ marginBottom: 16 }}>
          {menu.status === 'ACTIVE' ? 'Nonaktifkan' : 'Aktifkan'}
        </Button>
      )}

      <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-primary)', margin: '16px 0 8px' }}>Harga Jual</div>
      <DrawerField label="Harga Berlaku Saat Ini" value={currentPrice ? formatRupiah(currentPrice.price) : 'Belum tersedia'} />
      {prices && prices.length > 0 && (
        <table style={{ ...tableStyle, marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={thStyle}>Harga</th>
              <th style={thStyle}>Berlaku dari</th>
              <th style={thStyle}>Berlaku s/d</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((p) => (
              <tr key={p.menu_price_id}>
                <td style={tdStyle}>{formatRupiah(p.price)}</td>
                <td style={tdStyle}>{formatDate(p.effective_from)}</td>
                <td style={tdStyle}>{p.effective_to ? formatDate(p.effective_to) : 'Sekarang'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {prices && prices.length === 0 && <EmptyState text="Belum ada riwayat harga." variant="compact" />}
      {canEdit && (
        <form onSubmit={handleAddPrice} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <FieldLabel label="Harga baru (Rp)">
              <TextInput type="number" min="0" step="100" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} required />
            </FieldLabel>
          </div>
          <div style={{ width: 140 }}>
            <FieldLabel label="Berlaku mulai">
              <TextInput type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
            </FieldLabel>
          </div>
          <Button type="submit" variant="primary" loading={submitting}>
            Set
          </Button>
        </form>
      )}
      {priceError && <ErrorNote text={priceError} />}
    </Drawer>
  );
}
